import React from 'react';
import { Button, useDisclosure } from '@chakra-ui/react';

import { AssetType } from 'entities/assets/model';
import { AssetsDocument } from 'features/asset/add-assets-modal/graphql/queries.gen';
import { GetAssetsDocument } from 'pages/art-catalog/ui/ArtCatalogTable/queries.gen';
import { ButtonType, trackAssetDeleted } from 'shared/analytics';
import { useAppToast } from 'shared/hooks/toast';
import { TrashBinIcon } from 'shared/icons';
import { EntityDeletionAlert } from 'shared/ui/entity-deletion-alert';

import { useDeleteItemMutation } from './queries.gen';

interface DeleteItemButtonProps {
	id: string;
	onCompleted: () => void;
}

export const DeleteItemButton: React.FC<DeleteItemButtonProps> = ({
	id,
	onCompleted,
}) => {
	const { isOpen, onClose, onOpen } = useDisclosure();
	const toast = useAppToast();
	const [deleteItem, { loading }] = useDeleteItemMutation({
		variables: { id },
		onCompleted: (data) => {
			toast({ title: 'Item deleted successfully', status: 'success' });
			onCompleted();
			trackAssetDeleted({
				type: AssetType.ITEM,
				filename: data.deleteItem?.filename as string,
				link: data.deleteItem?.link as string,
			});
		},
		onError: () => toast({ title: 'Item was not deleted', status: 'error' }),
		refetchQueries: [AssetsDocument, GetAssetsDocument],
	});

	return (
		<>
			<Button
				disabled={loading}
				onClick={onOpen}
				leftIcon={<TrashBinIcon w={6} h={6} />}
			>
				Delete Item
			</Button>
			{isOpen && (
				<EntityDeletionAlert
					isOpen={isOpen}
					onClose={onClose}
					onSubmit={deleteItem}
					buttonName={ButtonType.DELETE_ITEM}
					title="Item Deletion"
					description="If you delete this item, it can't be restored. Are you sure you want to continue?"
				/>
			)}
		</>
	);
};
